import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckList, Field, KeyValue, StatusPill, type CheckItem, type KVRow, type StatusTone } from "@ozpanel/kit";
import { Button, Section, StateView, Tabs, TabsContent, TabsList, TabsTrigger, useAuth, useToast } from "@ozpanel/ui";
import { flattenMetrics, normaliseVerify, rawFiles, type RawFile, type VerifyState } from "./shapes";

// The generic module surface: what a module's config LOOKS LIKE on disk, what we INTEND it to
// be, whether they agree, what it measures, and whether it passes its own checks.
//
// Written once against the paths every module mounts generically rather than per module, which
// is the whole reason those paths exist. Six endpoints across 59 modules; the alternative is 59
// screens that drift apart.
//
// A tab is offered only when the module DECLARES its endpoint (the has_* flags on the module detail,
// matched against the module's own routes by the server). It used to ask all six of every module and
// read a 404 as "not offered" — six failing requests per visit, a broken endpoint indistinguishable
// from an absent one, and a "Set a parameter" tab on every module whose submit could only 404.

/** Which of the generic surfaces this module serves — from the module detail's has_* flags. */
export interface ConfigCaps {
  /** the config triad /config/{live,stored,diff} (has_config_diff) */
  config: boolean;
  raw: boolean;
  metrics: boolean;
  verify: boolean;
  param: boolean;
}

/** One file of /config/live or /config/stored — module.ConfigFile's JSON (internal/module/config.go), field
 *  for field; served by configLive/configStored in internal/server/modules.go, live adding not_loaded. */
interface ConfigFile {
  path: string;
  content: string;
  note?: string;
  missing?: boolean;
  /** For a file a service reads: whether it has loaded THIS content (absent = not knowable). */
  loaded?: boolean;
  loaded_by?: string;
  load_note?: string;
}

/** One file of /config/diff — module.FileDiff's JSON (internal/module/config.go). */
interface FileDiff {
  path: string;
  status: "in_sync" | "drift" | "only_live" | "only_stored" | "unreadable" | string;
  added: number;
  removed: number;
  detail?: string;
  lines?: { op: "same" | "add" | "remove" | string; line?: number; text: string }[];
}

/** /config/diff as configDiff (internal/server/modules.go) writes it. */
interface ConfigDiff {
  in_sync: boolean;
  drifted: number;
  not_loaded?: number;
  files: FileDiff[] | null;
  detail?: string;
}

// The path is passed WHOLE, not assembled from a fragment.
//
// The first version took a path fragment and built `/${module}/${path}`, which hides the endpoint
// from a reader and from the reachability scan alike — a call whose URL exists nowhere in the
// source is one nobody can grep for when asking "what talks to this route". Six literals cost
// nothing; an indirection that makes them invisible costs the thing that finds dead surface.
function useOptional<T>(url: string, key: string, module: string, enabled: boolean) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [key, module],
    queryFn: () => client.apiFetch<T>(url),
    enabled,
    retry: false,
  });
}

function FileList({ files, empty }: { files: (ConfigFile | RawFile)[]; empty: string }) {
  const [open, setOpen] = React.useState<string | null>(files[0]?.path ?? null);
  if (files.length === 0) return <div className="px-4 py-6 text-center text-[13px] text-muted">{empty}</div>;
  return (
    <div className="divide-y divide-line">
      {files.map((f) => {
        const cf = f as ConfigFile;
        return (
          <div key={f.path}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-surface2"
              onClick={() => setOpen(open === f.path ? null : f.path)}
            >
              <span className="min-w-0 break-all font-mono text-[12px]">{f.path}</span>
              <span className="flex shrink-0 items-center gap-2">
                {cf.missing && <StatusPill tone="warn">missing</StatusPill>}
                {/* A file on disk is not the running configuration until its daemon has read it. */}
                {cf.loaded === false && <StatusPill tone="warn">not loaded{cf.loaded_by ? ` by ${cf.loaded_by}` : ""}</StatusPill>}
                {cf.loaded === true && <StatusPill tone="ok">loaded{cf.loaded_by ? ` by ${cf.loaded_by}` : ""}</StatusPill>}
                <span className="text-[11.5px] text-muted">{f.content != null ? `${f.content.length} B` : ""}</span>
              </span>
            </button>
            {(cf.note || cf.load_note) && (
              <div className="px-4 pb-2 text-[11.5px] leading-relaxed text-muted">
                {cf.note}
                {cf.note && cf.load_note ? " · " : ""}
                {cf.load_note}
              </div>
            )}
            {open === f.path && (
              <pre className="max-h-[420px] overflow-auto whitespace-pre-wrap border-t border-line bg-bg2 px-4 py-3 font-mono text-[11.5px] leading-relaxed">
                {f.content || "(empty)"}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function ModuleConfigTab({ module, caps }: { module: string; caps: ConfigCaps }) {
  const live = useOptional<{ files: ConfigFile[] | null; total?: number; not_loaded?: number }>(`/${module}/config/live`, "cfg-live", module, caps.config);
  const stored = useOptional<{ files: ConfigFile[] | null; total?: number }>(`/${module}/config/stored`, "cfg-stored", module, caps.config);
  const diff = useOptional<ConfigDiff>(`/${module}/config/diff`, "cfg-diff", module, caps.config);
  const raw = useOptional<Record<string, unknown>>(`/${module}/raw`, "cfg-raw", module, caps.raw);
  const metrics = useOptional<Record<string, unknown>>(`/${module}/metrics`, "mod-metrics", module, caps.metrics);
  const verify = useOptional<Record<string, unknown>>(`/${module}/verify`, "mod-verify", module, caps.verify);

  const tabs: { key: string; label: string; node: React.ReactNode }[] = [];

  if (caps.config) {
    tabs.push({
      key: "config",
      label: "Configuration",
      node: (
        <div className="space-y-4">
          <StateView loading={diff.isLoading} error={diff.error} errorContext="the drift comparison" skeleton={<div className="h-16 animate-pulse rounded-card bg-surface2" />}>
            {diff.data && <DriftPanel diff={diff.data} />}
          </StateView>
          <Section title="Running on this node" hint={live.data?.total ? `${live.data.total} file(s)` : undefined} bodyClassName="p-0">
            <div className="px-4 pt-3 text-[12px] leading-relaxed text-muted">
              Read back from disk as the service sees it — not from what we believe we wrote.
              {!!live.data?.not_loaded && (
                <> <span className="text-warn">{live.data.not_loaded} file(s) are not loaded by the service that reads them.</span></>
              )}
            </div>
            <StateView loading={live.isLoading} error={live.error} errorContext="the live configuration">
              <FileList files={live.data?.files ?? []} empty="This module reports no live configuration." />
            </StateView>
          </Section>
          <Section title="What ozpanel intends" hint={stored.data?.total ? `${stored.data.total} file(s)` : undefined} bodyClassName="p-0">
            <div className="px-4 pt-3 text-[12px] leading-relaxed text-muted">
              What a re-render would produce. Where this differs from the live file, the live file
              is what is in force until something re-renders it.
            </div>
            <StateView loading={stored.isLoading} error={stored.error} errorContext="the intended configuration">
              <FileList files={stored.data?.files ?? []} empty="This module stores no rendered configuration." />
            </StateView>
          </Section>
        </div>
      ),
    });
  }

  if (caps.raw) {
    tabs.push({
      key: "raw",
      label: "Files",
      node: (
        <Section title="Configuration files" bodyClassName="p-0">
          <StateView loading={raw.isLoading} error={raw.error} errorContext="the configuration files">
            <FileList files={rawFiles(raw.data, module)} empty="No files." />
          </StateView>
        </Section>
      ),
    });
  }

  // A runtime PARAMETER, set without a restart — only where the module serves POST /{module}/param.
  if (caps.param) {
    tabs.push({
      key: "param",
      label: "Set a parameter",
      node: <ParamSetter module={module} />,
    });
  }

  if (caps.metrics) {
    const rows: KVRow[] = flattenMetrics(metrics.data).map((m) =>
      m.unread !== undefined
        ? { key: m.key, value: null, render: () => <StatusPill tone="warn">not measured: {m.unread}</StatusPill> }
        : { key: m.key, value: m.value },
    );
    tabs.push({
      key: "metrics",
      label: "Metrics",
      node: (
        <Section title="What this module measures" bodyClassName="p-0">
          <StateView loading={metrics.isLoading} error={metrics.error} errorContext="the metrics">
            {rows.length ? <KeyValue rows={rows} dense /> : <div className="px-4 py-6 text-center text-[13px] text-muted">The module answered with no values.</div>}
          </StateView>
        </Section>
      ),
    });
  }

  if (caps.verify) {
    const v = normaliseVerify(verify.data);
    const items: CheckItem[] = v.items.map((i) => ({ key: i.key, label: i.label, state: i.state, detail: i.detail }));
    tabs.push({
      key: "verify",
      label: "Verify",
      node: (
        <Section
          title="Does it pass its own checks"
          bodyClassName="p-0"
          actions={v.verdict !== "none" || v.verdictLabel ? <StatusPill tone={verdictTone(v.verdict)}>{v.verdictLabel || v.verdict}</StatusPill> : undefined}
        >
          <StateView loading={verify.isLoading} error={verify.error} errorContext="the self-check">
            {v.notes.length > 0 && (
              <div className="space-y-1 border-b border-line px-4 py-2.5 text-[12px] leading-relaxed">
                {v.notes.map((n, i) => (
                  <p key={i} className={n.tone === "fail" ? "text-crit" : n.tone === "warn" ? "text-warn" : "text-muted"}>{n.text}</p>
                ))}
              </div>
            )}
            <CheckList items={items} summarise emptyMessage="The module answered with no individual checks." />
          </StateView>
        </Section>
      ),
    });
  }

  if (tabs.length === 0) {
    // Said plainly. A module that implements none of these is not broken — most do not — and an
    // empty panel would read as a failure to load.
    return (
      <Section title="Configuration">
        <div className="text-[12px] leading-relaxed text-muted">
          This module does not publish its configuration, metrics or a self-check. Its settings, if
          it has any, are on the Settings tab.
        </div>
      </Section>
    );
  }
  const first = tabs[0]!;
  if (tabs.length === 1) return <>{first.node}</>;

  return (
    <Tabs defaultValue={first.key}>
      <TabsList>
        {tabs.map((t) => (
          <TabsTrigger key={t.key} value={t.key}>
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((t) => (
        <TabsContent key={t.key} value={t.key}>
          {t.node}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function verdictTone(s: VerifyState | "none"): StatusTone {
  return s === "ok" ? "ok" : s === "fail" ? "bad" : s === "none" ? "neutral" : "warn";
}

const DIFF_STATUS: Record<string, { tone: StatusTone; text: string }> = {
  drift: { tone: "warn", text: "differs" },
  only_live: { tone: "warn", text: "not generated by ozpanel" },
  only_stored: { tone: "bad", text: "missing on the node" },
  unreadable: { tone: "warn", text: "could not be read" },
};

// Drift, stated as a fact about what is IN FORCE rather than as a diff to read.
//
// Read from module.FileDiff: `status` per file (in_sync | drift | only_live | only_stored |
// unreadable) with added/removed counts and the changed lines. It read `same`/`differs`, which no
// server sends, so the changed-file list was always empty — a drifted module said "0 file(s) differ"
// beside a red pill. The server's own `detail` sentence is shown as given, including the case it
// names that a file comparison cannot: files that match but have not been loaded by their daemon.
function DriftPanel({ diff }: { diff: ConfigDiff }) {
  const changed = (diff.files ?? []).filter((f) => f.status !== "in_sync");
  const [open, setOpen] = React.useState<string | null>(null);
  const label = diff.in_sync
    ? "matches"
    : diff.drifted > 0
      ? `${diff.drifted} file(s) differ`
      : diff.not_loaded
        ? `${diff.not_loaded} not loaded`
        : "does not match";
  return (
    <Section title="Drift" bodyClassName="p-0" actions={<StatusPill tone={diff.in_sync ? "ok" : "warn"}>{label}</StatusPill>}>
      <div className="px-4 py-3 text-[12px] leading-relaxed text-muted">
        {diff.detail ??
          (diff.in_sync
            ? "What is running matches what ozpanel would render."
            : "The node is not running what ozpanel intends.")}
        {!diff.in_sync && (
          <span className="mt-1 block">
            The <span className="text-fg">live</span> file is what is in force — a correct stored copy
            changes nothing until something re-renders it, which is what makes this easy to miss.
          </span>
        )}
      </div>
      {changed.length > 0 && (
        <div className="divide-y divide-line border-t border-line">
          {changed.map((f) => {
            const st = DIFF_STATUS[f.status] ?? { tone: "warn" as StatusTone, text: f.status };
            return (
              <div key={f.path}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-surface2"
                  onClick={() => setOpen(open === f.path ? null : f.path)}
                >
                  <span className="min-w-0 break-all font-mono text-[12px]">{f.path}</span>
                  <span className="flex shrink-0 items-center gap-2 text-[11.5px] text-muted">
                    {(f.added > 0 || f.removed > 0) && (
                      <span className="font-mono">
                        <span className="text-ok">+{f.added}</span> <span className="text-crit">−{f.removed}</span>
                      </span>
                    )}
                    <StatusPill tone={st.tone}>{st.text}</StatusPill>
                  </span>
                </button>
                {f.detail && <div className="px-4 pb-2 text-[11.5px] text-muted">{f.detail}</div>}
                {open === f.path && (f.lines?.length ?? 0) > 0 && (
                  <pre className="max-h-[360px] overflow-auto border-t border-line bg-bg2 px-4 py-2 font-mono text-[11.5px] leading-relaxed">
                    {f.lines!.map((l, i) => (
                      <div key={i} className={l.op === "add" ? "text-ok" : l.op === "remove" ? "text-crit" : "text-muted"}>
                        {/* add = in the intended file, not running; remove = running, not intended */}
                        {l.op === "add" ? "+ " : l.op === "remove" ? "− " : "  "}
                        {l.text}
                      </div>
                    ))}
                  </pre>
                )}
              </div>
            );
          })}
          <div className="px-4 py-2 text-[11px] text-faint">+ in what ozpanel intends, not running · − running, not intended</div>
        </div>
      )}
    </Section>
  );
}

// POST /{module}/param {key, value}.
//
// Deliberately blunt: a free-text key and value, because the set of parameters differs per daemon
// and inventing a curated list here would quietly hide the one an operator came for. What it does
// NOT do is claim persistence — most of these apply to the RUNNING service and are lost on
// restart unless the same value is also in the module's settings, and saying so is the difference
// between a fix and a fix that disappears at 3am.
function ParamSetter({ module }: { module: string }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [key, setKey] = React.useState("");
  const [value, setValue] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<string | null>(null);

  const submit = () => {
    if (!key.trim()) return;
    setBusy(true);
    setResult(null);
    client
      .apiFetch<{ note?: string; applied?: boolean }>(`/${module}/param`, {
        method: "POST",
        body: JSON.stringify({ key: key.trim(), value }),
      })
      .then((r) => {
        notify({ kind: "success", title: `${key.trim()} set` });
        setResult(r?.note ?? "Applied to the running service.");
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Could not set that", body: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  return (
    <Section title="Set a parameter on the running service">
      <div className="flex max-w-3xl flex-wrap items-end gap-2">
        <div className="min-w-[12rem] flex-1">
          <Field label="Name" value={key} onChange={setKey} placeholder="e.g. smtpd_tls_auth_only" mono />
        </div>
        <div className="min-w-[10rem] flex-1">
          <Field label="Value" value={value} onChange={setValue} placeholder="yes" mono />
        </div>
        <Button size="sm" disabled={!key.trim() || busy} onClick={submit}>{busy ? "Setting…" : "Set"}</Button>
      </div>
      <p className="mt-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">
        This changes the RUNNING service. Unless the same value is also in this module's settings it
        is lost on the next restart — so use it to test a change, then put it where it will survive.
      </p>
      {result && <p className="mt-2 text-[12px] text-ok">{result}</p>}
    </Section>
  );
}
